/**
 * `v-synced` — show the server's value in an input without trampling typing.
 *
 * Why not `:value`: Vue re-assigns a bound `value` on EVERY render of the
 * component, not only when the bound expression changes. The drawer reads the
 * whole board (sub-tasks, rollups), so any change by anyone to any task
 * re-renders it — and every half-typed title or note snapped back to what the
 * server last said.
 *
 * This writes to the element only when the server value actually changed
 * since we last wrote it, and never into an element someone is typing in. The
 * text they have is theirs until `change` fires, which commits it. A switch to
 * a different task (a new `key`) always wins: that text belonged to the task
 * being left.
 *
 *   <input v-synced="{ key: task.id, value: task.title }" @change="…" />
 */
import type { ObjectDirective } from "vue";

type Field = HTMLInputElement | HTMLTextAreaElement;

interface Binding {
  key: string;
  value: string | number | null | undefined;
}

interface State {
  key: string;
  written: string;
}

const state = new WeakMap<Field, State>();

const text = (v: Binding["value"]): string => (v === null || v === undefined ? "" : String(v));

function write(el: Field, binding: Binding): void {
  const value = text(binding.value);
  if (el.value !== value) el.value = value;
  state.set(el, { key: binding.key, written: value });
}

export const vSynced: ObjectDirective<Field, Binding> = {
  mounted(el, { value }) {
    write(el, value);
  },
  updated(el, { value }) {
    const last = state.get(el);
    if (!last || last.key !== value.key) return write(el, value);

    const incoming = text(value.value);
    if (incoming === last.written) return;

    // Focused with unsaved edits: keep them, but remember the new server value
    // so we do not keep re-deciding on every render.
    if (document.activeElement === el && el.value !== last.written) {
      state.set(el, { key: value.key, written: incoming });
      return;
    }
    write(el, value);
  },
};
